import { useSWR } from '@tiger/swr';
import { Button } from 'antd';
import { createElement, FC, useEffect, useRef, useState } from 'react';
import { $Fieldutil, Field } from 'react-formutil';
import Media from 'react-media';
import Skeleton from 'components/Skeleton';
import API from 'utils/API';
import classlist from 'utils/classlist';
import Mobile from './Mobile';
import PC, { TelcodesSelectionProps } from './PC';
import './style.scss';

export interface TelcodeProps {
    name: string;
    defaultTelcode?: string | number; // 可以传入手机区码，也支持传入地区两位code，例如 US

    disabled?: boolean;
    onChange?(): void;
    type: 'telCountry' | 'bankCountry';
    size?: 'large' | 'middle' | 'small';
    className?: string;
}

// 需要读取的字段映射表，
// tel_code 国家数字码, country_code2 两位字符国家码, country_code 三位字符国家码
const countryValueKeyMap: Record<TelcodeProps['type'], TelcodesSelectionProps['countryValueKey']> = {
    telCountry: 'tel_code',
    bankCountry: 'country_code2'
};

const Telcode: FC<TelcodeProps> = ({ type, defaultTelcode, name, onChange, disabled, size, className }) => {
    const [country, setCountry] = useState<string>();
    const fieldutilRef = useRef<$Fieldutil>();
    const isTel = type === 'telCountry';
    const countryValueKey = countryValueKeyMap[type] || 'country_code';
    const {
        isLoading,
        data: countryList,
        error,
        mutate
    } = useSWR(
        () => ({
            url: isTel ? API.CUSTOMER.fields.country_us_group() : API.INFOBASE.fundBankCountryList(),
            params: { need_group: 1 }
        }),
        {
            dataChain: res => res.data,
            immutable: true
        }
    );

    useEffect(() => {
        if (countryList && defaultTelcode) {
            /**
             * 根据defaultTelcode查找对应的country信息
             */
            findCountry: for (const group of countryList) {
                for (const item of group.items) {
                    if (item[countryValueKey] === defaultTelcode) {
                        fieldutilRef.current!.$render(item[countryValueKey]);
                        setCountry(item.country);

                        break findCountry;
                    }
                }
            }
        }
    }, [countryList, defaultTelcode, countryValueKey]);

    return (
        <Field name={name} $defaultValue={defaultTelcode || '1'} $onFieldChange={onChange} $ref={fieldutilRef as any}>
            {$fieldutil => {
                return (
                    <div
                        className={classlist(
                            'telcode-component form-select',
                            disabled && 'disabled',
                            `form-select-${size}`,
                            className
                        )}>
                        {isLoading ? (
                            <Skeleton line={1} style={{ height: '100%' }} />
                        ) : !countryList && error ? (
                            <Button danger size="small" onClick={() => mutate()}>
                                {__('Retry')}
                            </Button>
                        ) : (
                            <span className="d-block text-truncate">
                                {isTel ? `+${$fieldutil.$viewValue}` : `${country || ''} ${$fieldutil.$viewValue}`}
                            </span>
                        )}

                        {!disabled && countryList && (
                            <Media query={{ maxWidth: 768 }}>
                                {matches =>
                                    createElement(matches ? Mobile : PC, {
                                        telcodes: countryList,
                                        value: $fieldutil.$viewValue,
                                        isTel,
                                        countryValueKey,
                                        onChange(item) {
                                            setCountry(item.country);

                                            $fieldutil.$render(item[countryValueKey]);
                                        }
                                    })
                                }
                            </Media>
                        )}
                    </div>
                );
            }}
        </Field>
    );
};

export default Telcode;
